import { Injectable, signal } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Popover } from 'primeng/popover';
import { BehaviorSubject, Subject } from 'rxjs';

import { Models } from '../../models/global.models';
import { applyTransaction } from '@datorama/akita';

// Set up interface for the store
interface UIStoreModel {
  activeLayoutID: string | null;
  activePipelineType: Models.PipelineType;
  cardOrganizeByID: keyof Models.PipelineLoanFile | null;
  gridPageSize: number | null;
  gridPageSizeAll: number | null;
  isLoading: boolean;
  lastRefreshDateTime: Date;
  quickActionSelection: Models.QuickActionType | null;
  selectedColumns: Models.ColumnData[];
  sidebarGridData: Models.PipelineLoanFile | Models.Lead | Models.LoanApplication | Models.PipelinePartner | null;
  sidebarVisible: boolean;
  priorityTag: Models.Tag;
  minMaxColumnValues: Models.MinMaxColumValuesMap;
  haveFeaturesInitialized?: boolean;
  portfolioOriginFilter?: Models.PortfolioOriginFilter;
  marketingOriginFilter?: Models.MarketingOriginFilter;
  activeFilterPartners?: boolean;
}

/**
 * Route only UI state
 * By default they are not injected in root since they are only needed by this route
 */
@Injectable({ providedIn: 'root' })
export class RouteUiService {
  refreshGrid$ = new BehaviorSubject(0);
  activeGridFilter$ = new BehaviorSubject<string>('');
  resizedColumns: string[] = [];
  closeDropdowns$ = new Subject<Popover>();
  openPrevious$ = new Subject<void>();
  openNext$ = new Subject<void>();
  selectedOpportunityPipelineLoanFilter = signal<Models.OpportunityPipelineLoanFilter>(Models.OpportunityPipelineLoanFilter.Portfolio);

  store = createStore(
    { name: 'pipeline' },
    withProps<UIStoreModel>({
      activeLayoutID: null,
      activePipelineType: Models.PipelineType.AllLoans,
      cardOrganizeByID: 'fileDataId',
      gridPageSize: null,
      gridPageSizeAll: 50,
      isLoading: false,
      lastRefreshDateTime: new Date(),
      quickActionSelection: null,
      selectedColumns: [],
      sidebarGridData: null,
      sidebarVisible: false,
      priorityTag: null,
      minMaxColumnValues: {} as Models.MinMaxColumValuesMap,
      haveFeaturesInitialized: false,
      portfolioOriginFilter: Models.PortfolioOriginFilter.OnlyPortfolio,
      marketingOriginFilter: Models.MarketingOriginFilter.OnlyMarketable,
      activeFilterPartners: true,
    }),
  );
  persistStore = persistState(this.store, { key: 'pipeline-ui', storage: localStorageStrategy });

  activeLayoutID$ = this.store.pipe(select((state) => state.activeLayoutID));
  activePipelineType$ = this.store.pipe(select((state) => state.activePipelineType));
  cardOrganizeByID$ = this.store.pipe(select((state) => state.cardOrganizeByID));

  /** Number of results per page on grid */
  gridPageSize$ = this.store.pipe(select((state) => state.gridPageSize));
  gridPageSizeAll$ = this.store.pipe(select((state) => state.gridPageSizeAll));
  isLoading$ = this.store.pipe(select((state) => state.isLoading));

  /** Timestamp of when data from API was last retrieved. */
  lastRefreshDateTime$ = this.store.pipe(select((state) => state.lastRefreshDateTime));
  quickActionSelection$ = this.store.pipe(select((state) => state.quickActionSelection));

  /** The visible columns on the current grid. */
  selectedColumns$ = this.store.pipe(select((state) => state.selectedColumns));
  sidebarVisible$ = this.store.pipe(select((state) => state.sidebarVisible));
  sidebarGridData$ = this.store.pipe(select((state) => state.sidebarGridData));

  priorityTag$ = this.store.pipe(select((state) => state.priorityTag));

  /** [Servicing loan]: Mininum and maximum values for heatmap on grid */
  minMaxColumnValues$ = this.store.pipe(select((state) => state.minMaxColumnValues));

  /** [Servicing loan]: show all loans including non-portfolio */
  portfolioOriginFilter$ = this.store.pipe(select((state) => state.portfolioOriginFilter));

  marketingOriginFilter$ = this.store.pipe(select((state) => state.marketingOriginFilter));

  /** [Partners]: show active or inactive partners */
  activeFilterPartners$ = this.store.pipe(select((state) => state.activeFilterPartners));

  stateChange(state: Partial<UIStoreModel>) {
    this.store.update((old) => ({ ...old, ...state }));
  }

  updatePorfolioOriginFilter(portfolioOriginFilter: Models.PortfolioOriginFilter) {
    this.store.update((state) => ({ ...state, portfolioOriginFilter }));
  }

  updateMarketingOriginFilter(marketingOriginFilter: Models.MarketingOriginFilter) {
    this.store.update((state) => ({ ...state, marketingOriginFilter }));
  }

  updateOpportunityLoanTypeFilter(opportunityLoanTypeFilter: Models.OpportunityPipelineLoanFilter) {
    this.selectedOpportunityPipelineLoanFilter.set(opportunityLoanTypeFilter);
    switch (opportunityLoanTypeFilter) {
      case Models.OpportunityPipelineLoanFilter.Portfolio:
        this._convertOpportunityLoanFilter();
        break;
      case Models.OpportunityPipelineLoanFilter.FundedSold:
        this._convertOpportunityLoanFilter(Models.PortfolioOriginFilter.OnlyNonPortfolio, Models.MarketingOriginFilter.OnlyNonMarketable);
        break;
      case Models.OpportunityPipelineLoanFilter.Ingested:
        this._convertOpportunityLoanFilter(Models.PortfolioOriginFilter.OnlyNonPortfolio, Models.MarketingOriginFilter.OnlyMarketable);
        break;
      default:
        this._convertOpportunityLoanFilter();
        break;
    }
  }

  private _convertOpportunityLoanFilter(
    portfolioOriginFilter = Models.PortfolioOriginFilter.OnlyPortfolio,
    marketingOriginFilter = Models.MarketingOriginFilter.OnlyMarketable,
  ) {
    applyTransaction(() => {
      this.store.update((state) => ({ ...state, portfolioOriginFilter }));
      this.store.update((state) => ({ ...state, marketingOriginFilter }));
    });
  }

  updateActiveFilterPartners(activeFilterPartners: boolean) {
    this.store.update((state) => ({ ...state, activeFilterPartners }));
  }
}
